// menuKeyboard — THE one keyboard model for every AI + chat dropdown
// (2026-09-02 per Nika "add keyboard navigation when ai chatbot dropdowns
// open + fix it for chat too").
//
// The house already had this behaviour in exactly ONE place: the shared
// right-click menu (components/ContextDropdown.js — `menuLevelNav` +
// `focusMenuOnOpen`) plus the per-opener focus nudge
// (workspace/functions/homeContextMenu.js `focusHomeContextMenu`). Every
// OTHER menu (the AI model switcher, the AI settings sections, the composer
// '+' attach menu, the assistant Sessions history) was pointer-only. This
// module is that same proven model, lifted out of one component's `scope` so
// four packages can share ONE implementation instead of growing four
// slightly-different copies.
//
// Why plain exported functions and not a component: `el.scope` only resolves
// inside the tree of the ONE top-level export it is declared on, which is why
// ContextDropdown already had to duplicate `menuLevelNav` between its two
// exports. Consumers here assign these functions as scope VALUES
// (`scope: { menuNav: menuArrowNav }` → `el.scope.menuNav(...)`), the same
// FA204-safe reach AiModelSwitcherBody already uses for the model catalog —
// a bare module-scope identifier read from inside a serialized factory reads
// as undefined after frank.toJSON (project_frank_drops_module_local_helpers).
//
// The contract every consumer implements:
//   • the panel carries `role="menu"` + `tabindex="-1"`,
//   • every actionable row carries `role="menuitem"`,
//   • the panel's `onKeydown` runs `menuArrowNav` first, then handles
//     Escape (close + `restoreMenuFocus`),
//   • the panel arms `menuFocusOnOpen` from `onRender`/`onUpdate` so DOM
//     focus actually lands inside the menu. That last step is not optional:
//     DOMQL's keydown delegate walks the ANCESTOR chain of `event.target`
//     (smbls/packages/element/src/delegate.js `installDelegatedEvent`), so a
//     menu that never receives focus never receives a keystroke either —
//     the exact root cause diagnosed three separate times before this
//     (workspace 99c1097a tickets pickers, a986e7ee chat, homeContextMenu.js).

// Rows a keyboard may land on. `[role="menuitem"]` is the house marker
// (ContextDropdown's own query); the option/radio/checkbox roles are accepted
// so a listbox-shaped picker can reuse the same walker.
const ROW_SELECTOR =
  '[role="menuitem"],[role="menuitemradio"],[role="menuitemcheckbox"],[role="option"]'

// Collect the focusable rows of ONE menu level, in DOM order — which is why a
// panel that renders two groups (the model rows AND the provider row, since
// 2026-09-02) needs no extra wiring: the arrow cycle traverses both because
// they are siblings in the same panel.
//
// Disabled rows are skipped, not focused-then-ignored: the AI menus render an
// unavailable provider as a DISABLED row that is still a real click target
// (so its reason stays readable on hover), and a keyboard cycle that stopped
// on unselectable rows would read as a broken menu.
export const menuRowNodes = (containerNode, selector) => {
  if (!containerNode || typeof containerNode.querySelectorAll !== 'function') return []
  const rows = Array.from(containerNode.querySelectorAll(selector || ROW_SELECTOR))
  return rows.filter((n) => {
    if (!n) return false
    if (n.hidden === true) return false
    if (typeof n.hasAttribute === 'function' && n.hasAttribute('disabled')) return false
    if (typeof n.getAttribute !== 'function') return true
    if (n.getAttribute('aria-disabled') === 'true') return false
    if (n.getAttribute('aria-hidden') === 'true') return false
    return true
  })
}

// ArrowUp / ArrowDown (wrapping) + Home / End within one menu level. Returns
// true when the key was consumed, so a caller can `if (nav(...)) return`
// before its own Enter/Escape branches.
//
// `opts.orientation: 'x'` swaps the pair to ArrowLeft/ArrowRight — the
// segmented "Applies to" control in the AI settings panel is a horizontal
// group, and up/down there would be a lie.
//
// idx < 0 (nothing in this level focused yet) lands on the FIRST row rather
// than `(-1 + 1) % n`, which would land on the second-to-last row on ArrowUp.
export const menuArrowNav = (e, containerNode, opts) => {
  if (!e || !e.key) return false
  const o = opts || {}
  const rows = menuRowNodes(containerNode, o.selector)
  if (!rows.length) return false
  const doc = typeof document !== 'undefined' ? document : null
  const active = o.activeElement || (doc && doc.activeElement) || null
  const idx = rows.indexOf(active)
  const nextKey = o.orientation === 'x' ? 'ArrowRight' : 'ArrowDown'
  const prevKey = o.orientation === 'x' ? 'ArrowLeft' : 'ArrowUp'
  let target = -1
  if (e.key === nextKey) target = idx < 0 ? 0 : (idx + 1) % rows.length
  else if (e.key === prevKey)
    target = idx < 0 ? rows.length - 1 : (idx - 1 + rows.length) % rows.length
  else if (e.key === 'Home') target = 0
  else if (e.key === 'End') target = rows.length - 1
  else return false
  if (typeof e.preventDefault === 'function') e.preventDefault()
  // Stop once handled so an ancestor menu level can never react to the same
  // keystroke (ContextDropdown's own rule).
  if (typeof e.stopPropagation === 'function') e.stopPropagation()
  const node = rows[target]
  if (node && typeof node.focus === 'function') node.focus()
  return true
}

// Enter / Space activation for rows that are NOT native <button>s. A real
// button already fires click on both keys, so consumers whose rows are
// buttons never call this. `e.target` IS the focused row (keydown fires on
// the focused node and bubbles to the panel), and `.click()` dispatches a
// real bubbling click — which is what DOMQL's delegated onClick listens for.
export const menuActivateRow = (e, containerNode) => {
  if (!e || (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar')) return false
  const row = e.target
  if (!row || typeof row.click !== 'function') return false
  if (containerNode && typeof containerNode.contains === 'function' && !containerNode.contains(row))
    return false
  if (typeof row.getAttribute === 'function' && !row.getAttribute('role')) return false
  if (typeof e.preventDefault === 'function') e.preventDefault()
  if (typeof e.stopPropagation === 'function') e.stopPropagation()
  row.click()
  return true
}

// Focus the first row of a just-opened menu, on a BOUNDED poll.
//
// A poll, not one microtask and not requestAnimationFrame: the panel does not
// exist (plugin-hosted) or has not left `visibility: hidden` (self-anchored)
// until the open state commits and paints, and Chrome throttles frames in
// backgrounded/automated tabs — the same reasoning ContextDropdown's ddSettle
// and homeContextMenu's focusHomeContextMenu already record.
//
// `getNode` may be a node or a function returning one (the node can appear
// mid-poll). `opts.when` gates each tick — consumers pass a visibility test so
// an always-mounted-but-hidden panel never steals focus.
// One scheduler for the polls below. `window.setTimeout` is the browser path;
// the bare `setTimeout` fallback exists because this package's own test DOM
// shim (__tests__/testDomShim.js) installs an INERT `window` carrying only
// `document` — a hard `window.setTimeout(...)` would throw there and take
// down suites that merely touch a component wired to these helpers.
const _schedule = (fn, ms) => {
  if (typeof window !== 'undefined' && typeof window.setTimeout === 'function')
    return window.setTimeout(fn, ms)
  if (typeof setTimeout === 'function') return setTimeout(fn, ms)
  return null
}

export const focusFirstMenuRow = (getNode, opts) => {
  if (typeof window === 'undefined') return
  const o = opts || {}
  const tries = o.tries || 10
  const every = o.every || 30
  let n = 0
  const tick = () => {
    if (typeof o.cancelled === 'function' && o.cancelled()) return
    const node = typeof getNode === 'function' ? getNode() : getNode
    const ready = node && (typeof o.when !== 'function' || o.when(node))
    if (ready) {
      const doc = typeof document !== 'undefined' ? document : null
      const rows = menuRowNodes(node, o.selector)
      const first = o.last ? rows[rows.length - 1] : rows[0]
      // CONFIRM the focus landed, don't assume it (measured live 2026-09-02
      // on the composer '+' menu): `.focus()` on a node that is not attached
      // or not painted yet is a SILENT no-op, and the first poll tick can
      // easily run in that window — the menu then opened correctly, carried
      // its rows, and still had focus parked outside it. Keep polling until
      // the browser agrees, instead of declaring victory on the call.
      if (first && typeof first.focus === 'function') {
        first.focus()
        if (!doc || doc.activeElement === first) return
      }
      // Painted with no rows at all (an empty menu) — park focus on the
      // panel itself so Escape is still reachable through the delegate walk.
      if (rows.length === 0 && typeof node.focus === 'function') {
        node.focus()
        if (!doc || doc.activeElement === node) return
      }
    }
    if (++n < tries) _schedule(tick, every)
  }
  _schedule(tick, 0)
}

// One generation counter per panel. Every arm bumps it and captures the new
// value; a poll whose captured value is stale cancels itself on its next
// tick. That is what stops a menu opened-then-closed inside the poll window
// from pulling focus back into a panel the user has already left.
const _bumpMenuFocusGen = (el) => {
  el.__menuFocusGen = (el.__menuFocusGen || 0) + 1
  return el.__menuFocusGen
}

// Arm the open edge WITHOUT any edge bookkeeping: remember what had focus,
// then poll focus into the first (or `opts.last`) row.
//
// Split out of `menuFocusOnOpen` (below) because that function's `_menuWasOpen`
// latch only makes sense for a panel whose hook can re-fire spuriously — an
// always-mounted panel arming from `onRender` + every `onUpdate`. A panel that
// is `if:`-gated has the OPPOSITE problem: smbls fires NO lifecycle hook on
// its open edge at all (see the CanvasPromptTextarea AttachMenu note), so the
// site that flips the open flag must arm the focus itself. That site IS the
// edge — a latch there would only swallow the second open of the session.
export const armMenuFocus = (el, opts) => {
  if (!el || typeof window === 'undefined') return
  const doc = typeof document !== 'undefined' ? document : null
  const active = doc && doc.activeElement
  el._menuReturnFocusTo = active && active !== doc.body ? active : null
  const gen = _bumpMenuFocusGen(el)
  focusFirstMenuRow(() => el.node, {
    ...(opts || {}),
    cancelled: () => gen !== el.__menuFocusGen
  })
}

// Arm a menu panel that is a SIBLING (or an uncle) of `el`, addressed by its
// DOMQL key — the shape every "hand focus into the open menu" call site needs.
//
// `el.lookup(key)` walks the ANCESTOR chain and checks each ancestor's named
// children, so a '+' button inside a toolbar and a textarea two levels away
// both resolve the same panel. It works while the panel is CLOSED too: an
// `if:`-hidden child is still published on its parent under its key
// (smbls create.js, the `if (!ref.__if)` branch), so the poll can start before
// the node exists and land the moment it paints.
//
// Returns true when a panel was found, so a caller can decide whether it
// consumed the key. `menuNodeVisible` is the gate — an always-mounted-but-
// hidden panel, or one under a display:none ancestor, never steals focus.
export const armNamedMenuFocus = (el, key, opts) => {
  const panel = el && typeof el.lookup === 'function' ? el.lookup(key) : null
  if (!panel) return false
  armMenuFocus(panel, { ...(opts || {}), when: (node) => menuNodeVisible(node) })
  return true
}

// Close the menu WITHOUT returning focus: drop the stored return target and
// cancel any in-flight arm poll. For the close paths that move focus somewhere
// else on purpose — Tab (the browser owns the next stop), a row that puts the
// caret back in the composer, a row that opens a file picker. Without the
// cancel, an arm fired ~30ms earlier would still be polling and would fight
// whatever the row just focused.
export const releaseMenuFocus = (el) => {
  if (!el) return
  el._menuReturnFocusTo = null
  _bumpMenuFocusGen(el)
}

// Return focus to whatever had it when the menu opened (the trigger, in
// every case here). Safe to call twice — the second call is a no-op, because
// a menu can close through two paths at once (Escape handler AND the
// open→closed edge below).
export const restoreMenuFocus = (el) => {
  if (!el) return false
  const back = el._menuReturnFocusTo
  el._menuReturnFocusTo = null
  // Same cancel as releaseMenuFocus: a still-running arm poll would otherwise
  // pull focus off the trigger we just restored it to.
  _bumpMenuFocusGen(el)
  if (back && typeof back.focus === 'function') {
    back.focus()
    return true
  }
  return false
}

// The open/close focus lifecycle, edge-detected so it fires EXACTLY ONCE per
// false→true (or true→false) transition. Same `_wasOpen` / `_returnFocusTo`
// shape as ContextDropdown.focusMenuOnOpen and AppDialog's own focus sync —
// an onUpdate that re-ran on every unrelated state tick would otherwise yank
// focus back to the first row while the user is three rows down.
export const menuFocusOnOpen = (el, isOpen, opts) => {
  if (!el || typeof window === 'undefined') return
  const open = !!isOpen
  if (open === el._menuWasOpen) return
  el._menuWasOpen = open
  if (!open) {
    restoreMenuFocus(el)
    return
  }
  armMenuFocus(el, opts)
}

// Is this panel REALLY on screen right now? The self-anchored AI panels stay
// mounted and hide with `visibility: hidden` (they keep layout, so
// offsetWidth is a useless signal), while plugin-hosted panels are mounted
// only while open. One predicate covers both hosts, so a body rendered in
// either place arms its focus at the right moment and never before.
export const menuNodeVisible = (node) => {
  if (!node || typeof window === 'undefined') return false
  // A node still detached from the document is never "on screen", and
  // getComputedStyle returns an EMPTY declaration for it (every property
  // reads ''), which would otherwise pass all three checks below.
  if (node.isConnected === false) return false
  if (typeof window.getComputedStyle !== 'function') return true
  const st = window.getComputedStyle(node)
  if (!st) return true
  if (st.visibility === 'hidden' || st.display === 'none' || st.opacity === '0') return false
  // An ANCESTOR's `display: none` does NOT show up in this node's own
  // computed style — it still reports `display: flex` — but it collapses the
  // box to 0×0 and makes every descendant unfocusable. Measured live
  // 2026-09-02: the composer '+' menu on a route where the whole composer is
  // hidden reported itself visible, and `.focus()` was silently refused.
  // Both signals are needed: a `visibility: hidden` panel keeps its box (so
  // size alone would pass it), a display-none ancestor keeps the computed
  // style (so style alone would pass it).
  if (typeof node.offsetWidth === 'number' && !node.offsetWidth && !node.offsetHeight) return false
  return true
}
